import axios from 'axios';

// Configure axios instance
const api = axios.create({
    // CHANGE THIS: Point to the Nginx /api tunnel automatically
    baseURL: '/api',

    timeout: 10000, // 10 seconds
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
    },
    // Since we are using Nginx Reverse Proxy, the browser sees 
    // Frontend & Backend as the SAME domain.
    // 'withCredentials' ensures cookies/sessions work correctly.
    withCredentials: true,
});

// Request interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token && !config.url.includes('/auth/')) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        // Log request details
        console.log(`[API] ${config.method.toUpperCase()} ${config.url}`, {
            params: config.params,
            headers: config.headers,
            data: config.data
        });

        return config;
    },
    (error) => {
        console.error('[API] Request Error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        // Log successful response
        console.log(`[API] ${response.status} ${response.config.method.toUpperCase()} ${response.config.url}`, {
            data: response.data,
            headers: response.headers
        });
        return response;
    },
    (error) => {
        const errorMessage = error.response?.data?.message || error.message;
        const status = error.response?.status;
        const url = error.config?.url;

        // Log detailed error information
        console.error(`[API] Error ${status} ${error.config?.method?.toUpperCase()} ${url}:`, {
            message: errorMessage,
            response: error.response?.data,
            headers: error.response?.headers,
            config: {
                headers: error.config?.headers,
                params: error.config?.params,
                data: error.config?.data
            }
        });

        // Handle 401 Unauthorized
        if (status === 401) {
            if (window.location.pathname !== '/login') {
                console.warn('[API] Unauthorized access - redirecting to login');
                localStorage.removeItem('token');
                window.location.href = '/login';
            }
            return Promise.reject({
                message: 'Your session has expired. Please log in again.',
                status: 401,
                redirect: true
            });
        }

        // Handle 403 Forbidden
        if (status === 403) {
            return Promise.reject({
                message: 'You do not have permission to perform this action.',
                status: 403
            });
        }

        // Handle 404 Not Found
        if (status === 404) {
            return Promise.reject({
                message: 'The requested resource was not found.',
                status: 404
            });
        }

        // Handle 500 Internal Server Error
        if (status >= 500) {
            return Promise.reject({
                message: 'A server error occurred. Please try again later.',
                status: status
            });
        }

        // Default error handling
        return Promise.reject({
            message: errorMessage || 'An unexpected error occurred',
            status: status,
            data: error.response?.data
        });
    }
);

// Document URLs API
export const documentApi = {
    getInvoiceDocumentUrl: (id) => api.get(`/invoices/document-url/${id}`),
    getTdsChallanDocumentUrl: (id) => api.get(`/tds/batches/${id}/document-url`),
    downloadTdsChallanZip: (challanIds, downloadAll) => api.post('/tds/challans/download-zip', { challanIds, downloadAll }, { responseType: 'blob' })
};

// TDS Rules API
export const tdsApi = {
    // Get all TDS rules
    getAllTdsRules: () => api.get('/vendor-service/api/tds-rules'),

    // Get TDS rule by code
    getTdsRuleByCode: (code) => api.get(`/vendor-service/api/tds-rules/${code}`)
};

// Invoice API
export const invoiceApi = {
    downloadAuditZip: (year) =>
        api.get(`/invoices/audit/download?year=${year}`, { responseType: 'blob' }),
    getAuditYears: () => api.get('/invoices/audit/years'),
    downloadInvoicesZip: (invoiceIds) =>
        api.post('/invoices/download-zip', invoiceIds, { responseType: 'blob' }),
    getInvoicesByPaymentBatch: (batchId) => api.get(`/invoices/by-payment-batch/${batchId}`),
    getPaymentNotificationPreview: (id) => api.get(`/invoices/${id}/payment-notification-preview`),
    sendPaymentNotification: (id) => api.post(`/invoices/${id}/send-payment-notification`),
};

// Payment Batch API
export const paymentBatchApi = {
    getAllBatches: () => api.get('/payment-batches'),
    getBatch: (id) => api.get(`/payment-batches/${id}`),
    createBatch: (invoiceIds, createdBy) => api.post('/payment-batches', invoiceIds, { params: { createdBy } }),
    deleteBatch: (id) => api.delete(`/payment-batches/${id}`),
    markAsPaid: (id, paymentDetails) => api.post(`/payment-batches/${id}/pay`, paymentDetails),
    download: (id, type) => api.get(`/payment-batches/${id}/download`, { params: { type }, responseType: 'blob' })
};

// Export the configured instance
export default api;